import { isProductionPartnerHost } from "../../../utils/partnerDomains";

/**
 * Mapping from Partner UUID (from auth system) to Vendor Name (for Home Nursing API)
 *
 * This mapping is necessary because:
 * 1. Partner names in old backend (auth) may not match new backend vendor names exactly
 * 2. We need to normalize vendor names to match what the Home Nursing API expects
 *
 * Data provided by backend team on 2026-01-01
 */

export const PARTNER_UUID_TO_VENDOR: Record<string, string> = {
  // Omega Home Health Care
  '37f1e4cc-4c69-4eb5-851f-713cbd8c763c': 'Omega Home Health Care',

  // Lana Life Care LLC
  '0789b23b-e845-475e-959a-c041049939a3': 'Lana Life Care',

  // Prime Care FZC LLC
  '24c523f8-4917-4984-a2a3-00ddc3c5c690': 'Prime Care FZC',

  //dev testing id
  "e4c53224-1c94-4169-9638-cf4c72cf115b":'Prime Care FZC',

  // Lifeline UAE
  '19c80fc6-1815-4e9e-8f2d-aa62e7a0c2db': 'Lifeline UAE',

  // Florida point home health care
  '92451295-12c5-4dce-8bd2-7a13895008a9': 'Florida Point Home Health Care',

  // Wahat Al Aman
  '2fbb0c8f-eae5-403b-a4e0-abc8a4b8e180': 'Wahat Al Aman',
  
  //Salwaty
   'a37de659-f698-408d-84b7-69f115873dd1':'Salwaty Home Healthcare',

  // DMETCO Bayti
  '2f0646fc-d302-4190-88a6-da18389f06a6': 'DMETCO Bayti',

  // 24Care
  'bb0c0d55-7e07-486d-ba6a-28d72cdc5805': '24Care'
};

/**
 * Vendors that have access to the Caregiver Calendar feature
 * Names must match exactly as shown in the API
 */
export const CALENDAR_ENABLED_VENDORS: string[] = [
  'Wahat Al Aman',
  'Omega Home Health Care',
  'Lana Life Care',
  'Prime Care FZC',
  'Lifeline UAE',
  'Florida Point Home Health Care',
  'Salwaty Home Healthcare',
];

/**
 * Partners that should NOT see Home Nursing Revenue Tracker.
 */
const REVENUE_TRACKER_DISABLED_PARTNER_UUIDS = new Set<string>([
  'bb0c0d55-7e07-486d-ba6a-28d72cdc5805', // 24Care
]);

/**
 * Get vendor name from partner UUID
 * @param partnerUUID - UUID from auth system (state.authReducer.setting.partnerUUID)
 * @returns Vendor name for API call, or null if not found
 */
export function getVendorFromPartnerUUID(partnerUUID: string | undefined): string | null {
  if (!partnerUUID) {
    return null;
  }

  return PARTNER_UUID_TO_VENDOR[partnerUUID] || null;
}

/**
 * Alias to keep naming consistent in Home Nursing context.
 * @param partnerUUID - UUID from auth system (state.authReducer.setting.partnerUUID)
 */
export function getVendorNameFromPartnerUUID(partnerUUID: string | undefined): string | null {
  return getVendorFromPartnerUUID(partnerUUID);
}

/**
 * Returns true when running on the live partner domain.
 * Use this to gate features only for production partners.
 */
export function isPartnerLiveHost(hostname?: string): boolean {
  return isProductionPartnerHost(hostname);
}

/**
 * Returns true when running on localhost (local dev only).
 */
export function isLocalHost(hostname?: string): boolean {
  if (!hostname) return false;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "::1"
  );
}

/**
 * Returns true when hitting the production Home Nursing API.
 */
export function isLiveHomeNursingApiBaseUrl(baseUrl?: string): boolean {
  if (!baseUrl) return false;
  return baseUrl.startsWith("https://homenursing-api-prod.happypebble-41284363.southindia.azurecontainerapps.io");
}

/**
 * Determine if Home Nursing tabs (Care Plan, Revenue Tracker) should be shown.
 * - In dev/local: always show.
 * - In live partner domain: only show when vendor mapping exists.
 */
export function shouldShowHomeNursingTabs({
  partnerUUID,
  hostname,
  apiBaseUrl,
  isDev,
}: {
  partnerUUID?: string;
  hostname?: string;
  apiBaseUrl?: string;
  isDev?: boolean;
}): boolean {
  if (isDev) {
    return true;
  }

  if (!isPartnerLiveHost(hostname) && !isLiveHomeNursingApiBaseUrl(apiBaseUrl)) {
    return true;
  }
  return Boolean(getVendorNameFromPartnerUUID(partnerUUID));
}

/**
 * Determine if Home Nursing Revenue Tracker should be shown.
 * - Hidden for specific partners, even if other Home Nursing tabs are visible.
 */
export function shouldShowHomeNursingRevenueTracker({
  partnerUUID,
  hostname,
  apiBaseUrl,
  isDev,
}: {
  partnerUUID?: string;
  hostname?: string;
  apiBaseUrl?: string;
  isDev?: boolean;
}): boolean {
  if (partnerUUID && REVENUE_TRACKER_DISABLED_PARTNER_UUIDS.has(partnerUUID)) {
    return false;
  }
  return shouldShowHomeNursingTabs({ partnerUUID, hostname, apiBaseUrl, isDev });
}

/**
 * Resolve vendor name for Home Nursing API calls.
 * - In dev/local: allow a provided fallback.
 * - In live partner domain: require mapped vendor.
 */
export function resolveHomeNursingVendorName({
  partnerUUID,
  hostname,
  apiBaseUrl,
  fallbackVendor,
  isDev,
}: {
  partnerUUID?: string;
  hostname?: string;
  apiBaseUrl?: string;
  fallbackVendor?: string;
  isDev?: boolean;
}): string | null {
  const isLocal = isLocalHost(hostname);
  if (isLocal && fallbackVendor) {
    return fallbackVendor;
  }
  const mappedVendor = getVendorNameFromPartnerUUID(partnerUUID);
  if (mappedVendor) return mappedVendor;
  if (isLocal) {
    return fallbackVendor ?? null;
  }
  if (!isPartnerLiveHost(hostname) && !isLiveHomeNursingApiBaseUrl(apiBaseUrl)) {
    return fallbackVendor ?? null;
  }
  return null;
}

/**
 * Check if a vendor has access to the Caregiver Calendar feature
 * @param vendorName - Vendor name (from getVendorFromPartnerUUID)
 * @returns true if vendor has calendar access, false otherwise
 */
export function hasCalendarAccess(vendorName: string | null): boolean {
  if (!vendorName) {
    return false;
  }

  return CALENDAR_ENABLED_VENDORS.includes(vendorName);
}
